/**
 * External dependencies
 */
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * Internal dependencies
 */
import { Card, CardContent } from '@Components/Card';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import Loader from '@Components/Loader';
import service from '../../service';
import { useInboxManagement } from '@Hooks/useInboxManagement';
import { useUsers } from '@Hooks/useUsers';
import type { User } from '@Types/user';
import Select from 'react-select';
import { usePhoneNumbers } from '@Hooks/usePhoneNumbers';

const INBOX_TYPES = ['SMS', 'EMAIL', 'PUSH', 'APP'] as const;
type InboxTypeValue = (typeof INBOX_TYPES)[number];

type NumberOption = { value: string; label: string; phoneNumber: string };

const SELECT_CLASS =
  'border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[3px]';

function CreateInboxDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [inboxType, setInboxType] = useState<InboxTypeValue>('SMS');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');

  const { data: usersResponse } = useUsers({ searchParams: { 'page[size]': 200 } });
  const users: User[] = usersResponse?.data ?? [];

  const { data: phoneNumbers } = usePhoneNumbers();
  // Only unassigned, active dedicated numbers can back a new staff SMS inbox.
  const availableNumbers = (phoneNumbers ?? []).filter(
    (n) => n.status === 'ACTIVE' && n.kind === 'DEDICATED' && !n.inboxId,
  );
  const numberOptions: NumberOption[] = availableNumbers.map((n) => ({
    value: n.id,
    label: n.region ? `${n.phoneNumber} (${n.region})` : n.phoneNumber,
    phoneNumber: n.phoneNumber,
  }));

  const reset = () => {
    setInboxType('SMS');
    setPhoneNumber('');
    setPhoneNumberId('');
    setEmail('');
    setUserId('');
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const inbox = await service.createInbox({
        inboxType,
        phoneNumber: phoneNumber.trim() || undefined,
        email: email.trim() || undefined,
        userId: userId || undefined,
      });
      // Bind the owned number to the new inbox so PhoneNumber.inboxId is set
      // (mirrors onto Inbox.phoneNumber) - keeps the Numbers view and the
      // stylist-sender resolver consistent, rather than leaving a loose string.
      if (inboxType === 'SMS' && phoneNumberId && inbox?.id) {
        await service.assignPhoneNumber(phoneNumberId, inbox.id);
      }
      return inbox;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-management'] });
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      toastSuccess('Inbox created.');
      reset();
      setOpen(false);
    },
    onError: (e) =>
      toastError(e instanceof Error ? e.message : 'Failed to create inbox'),
  });

  const canSubmit =
    !createMutation.isPending &&
    (inboxType !== 'SMS' || phoneNumber.trim().length > 0) &&
    (inboxType !== 'EMAIL' || email.trim().length > 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>Create Inbox</Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(event) => {
          // The phone-number menu is portalled outside the dialog; without this
          // guard Radix treats clicking an option as an outside interaction and
          // eats the selection / dismisses the dialog.
          const target = event.detail.originalEvent.target as HTMLElement | null;
          if (target?.closest('.rs__menu-portal')) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Create Inbox</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) createMutation.mutate();
          }}
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-zinc-700">Type</label>
            <select
              className={SELECT_CLASS}
              value={inboxType}
              onChange={(e) => setInboxType(e.target.value as InboxTypeValue)}
            >
              {INBOX_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {inboxType === 'SMS' && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-zinc-700">
                Phone number *
              </label>
              <Select<NumberOption>
                options={numberOptions}
                value={
                  numberOptions.find((o) => o.value === phoneNumberId) ?? null
                }
                onChange={(opt) => {
                  setPhoneNumberId(opt?.value ?? '');
                  setPhoneNumber(opt?.phoneNumber ?? '');
                }}
                isClearable
                placeholder={
                  availableNumbers.length
                    ? 'Select a number…'
                    : 'No unassigned numbers — register one first'
                }
                classNamePrefix="rs"
                menuPlacement="auto"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 10000,
                    // Radix modal sets body { pointer-events: none }; the menu is
                    // portalled to body, so re-enable clicks on its options.
                    pointerEvents: 'auto',
                  }),
                }}
              />
            </div>
          )}

          {inboxType === 'EMAIL' && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-zinc-700">Email *</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. concierge@example.com"
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-zinc-700">
              Owner (optional)
            </label>
            <select
              className={SELECT_CLASS}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">System (no owner)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email || u.id}
                </option>
              ))}
            </select>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function InboxManagement() {
  const { data: inboxes, status } = useInboxManagement();
  const rows = inboxes ?? [];

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/inbox-management' },
          { name: 'Inbox Management', href: '/inbox-management' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-start justify-between gap-4">
        <div>
          <Heading>Inbox Management</Heading>
          <p className="text-sm text-zinc-500">
            All staff and system inboxes — owner, type, number, and message
            volume.
          </p>
        </div>
        <CreateInboxDialog />
      </div>
      <Card>
        <CardContent>
          {status === 'pending' ? (
            <Loader size="lg" />
          ) : !rows.length ? (
            <div className="py-8 text-center text-sm text-zinc-500">
              No inboxes found.
            </div>
          ) : (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Owner</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Number</TableHead>
                  <TableHead>Inbound</TableHead>
                  <TableHead>Outbound</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((inbox) => (
                  <TableRow key={inbox.id}>
                    <TableCell className="font-medium">
                      {inbox.ownerName}
                      {inbox.ownerType === 'SYSTEM' && (
                        <span className="ml-2 text-xs text-zinc-400">
                          System
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{inbox.inboxType}</Badge>
                    </TableCell>
                    <TableCell>
                      {inbox.phoneNumber || inbox.email || 'N/A'}
                    </TableCell>
                    <TableCell>{inbox.inboundCount}</TableCell>
                    <TableCell>{inbox.outboundCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
